import React from 'react'
import { useSelector } from 'react-redux'
import { GoogleIcon, AppleIcon, SlackIcon, Icon } from '@slite/icons'

import { apiUrl, appPath, getConfig } from 'config'
import {
  getOrgCreationFlow,
  getStructureCacheKeyFromUrl,
  isAskXDesktop,
  isDesktop,
  useIsSuperEnabledFromUrl,
} from '../utils'
import { Button } from '.'
import { FROM_MOBILE_APP_KEY, getItemSafe } from '../helpers/localStorage'
import { IconButton } from '@slite/components'
import { getReferralToken } from '../helpers/referralToken'
import { FormattedMessage } from 'react-intl'
import { SSOProviders } from './SSOProviders'

type OnClickCB = (cb: () => void) => void
type QueryArgs = {
  rejoin?: string
  inviteToken?: string
  organizationInviteToken?: string
  orgCreationFlow?: string
  restrictToDomains?: string
  joinOrigin?: string
  channelToken?: string
  organizationId?: string
  structureCacheKey?: string
}

function getSearchParams({ superEnabled }: { superEnabled?: boolean } = {}) {
  const fromMobileApp = getItemSafe(FROM_MOBILE_APP_KEY)
  const isDesktopApp = isDesktop()
  const isAskXDesktopApp = isAskXDesktop()
  const getLoginOriginValue = () => {
    if (fromMobileApp === 'true') {
      return 'mobile'
    } else if (isDesktopApp) {
      return 'desktop'
    } else if (isAskXDesktopApp) {
      return 'super-desktop'
    }
    return 'app'
  }
  const urlParams = new URLSearchParams({
    loginOrigin: getLoginOriginValue(),
  })

  const referralToken = getReferralToken()
  if (referralToken) {
    urlParams.set('referralToken', referralToken)
  }

  if (superEnabled) {
    urlParams.set('superEnabled', 'true')
  }

  return urlParams.toString()
}

function appendSearchParams(
  url: URL,
  {
    rejoin,
    inviteToken,
    organizationInviteToken,
    restrictToDomains = '',
    joinOrigin,
    channelToken,
    config,
    organizationId,
    orgCreationFlow,
    structureCacheKey,
  }: Pick<
    QueryArgs,
    | 'rejoin'
    | 'inviteToken'
    | 'organizationInviteToken'
    | 'restrictToDomains'
    | 'joinOrigin'
    | 'channelToken'
    | 'organizationId'
    | 'orgCreationFlow'
    | 'structureCacheKey'
  > & {
    config: { appPath: string }
  }
) {
  const params = {
    appPath: config.appPath,
    channelToken,
    organizationId,
    invite: inviteToken,
    rejoin,
    restrictToDomains,
    organizationInviteToken,
    joinOrigin,
    orgCreationFlow,
    structureCacheKey,
  }

  ;(Object.keys(params) as (keyof typeof params)[])
    .filter((key) => params[key])
    .forEach((key) => url.searchParams.append(key, params[key] as string))

  return url
}

function OAuthLink({
  OauthIcon,
  children,
  pathname,
  onClick,
  className,
  iconOnly,
  ...props
}: Omit<SignInWithProviderProps, 'provider'> & {
  OauthIcon?: Icon
  children: React.ReactNode
  pathname: string
  className?: string
}) {
  const config: { apiUrl: string; appPath: string } = useSelector(getConfig)
  const orgCreationFlow = getOrgCreationFlow(appPath) ?? undefined
  const isDesktopApp = isDesktop()
  const isAskXDesktopApp = isAskXDesktop()
  const structureCacheKey = getStructureCacheKeyFromUrl()

  let url: URL
  /* Needs this fallback as apiUrl is not always a valid base URL */
  try {
    url = new URL(pathname, apiUrl)
  } catch {
    url = new URL(pathname, window.location.origin)
  }
  appendSearchParams(url, {
    ...props,
    config,
    orgCreationFlow,
    structureCacheKey,
  })
  const onOauthClick = React.useCallback(() => {
    if (isDesktopApp) {
      // on desktop we have to open a new window, otherwise we might try an oauth auth flow on electron, which doesnt work
      window.open(url.toString(), '_top', 'noreferrer,noopener')
    } else if (isAskXDesktopApp) {
      if ('todesktop' in window) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(window.todesktop as any).contents.openUrlInBrowser(url.toString())
      }
    } else {
      window.location.href = url.toString()
    }
  }, [isDesktopApp, url, isAskXDesktopApp])

  if (OauthIcon && iconOnly) {
    return (
      <IconButton
        onClick={onOauthClick}
        className={className}
        type="button"
        icon={OauthIcon}
        variant="tertiary"
      />
    )
  }

  return (
    <Button
      onClick={onOauthClick}
      className={className}
      variant="secondary"
      icon={OauthIcon}
      type="button"
    >
      {children}
    </Button>
  )
}

function Google(props: Omit<SignInWithProviderProps, 'provider'>) {
  const superEnabled = useIsSuperEnabledFromUrl()
  const loginOrigin = getSearchParams({ superEnabled })
  const pathname =
    props.organizationId || props.organizationInviteToken
      ? `/api/auth/google/login`
      : '/api/auth/google/signup'
  return (
    <OAuthLink
      {...props}
      OauthIcon={GoogleIcon}
      pathname={`${pathname}?${loginOrigin}`}
    >
      <FormattedMessage
        id="boarding.signInWith.google"
        defaultMessage="Continue with Google"
        values={{}}
      />
    </OAuthLink>
  )
}

function Slack(props: Omit<SignInWithProviderProps, 'provider'>) {
  const superEnabled = useIsSuperEnabledFromUrl()
  const loginOrigin = getSearchParams({ superEnabled })
  const pathname =
    props.organizationId || props.organizationInviteToken
      ? `/api/auth/slack/login`
      : '/api/auth/slack/signup'
  return (
    <OAuthLink
      {...props}
      OauthIcon={SlackIcon}
      pathname={`${pathname}?${loginOrigin}`}
    >
      <FormattedMessage
        id="boarding.signInWith.slack"
        defaultMessage="Continue with Slack"
        values={{}}
      />
    </OAuthLink>
  )
}

function OpenId(
  props: Omit<
    Extract<SignInWithProviderProps, { provider: SSOProviders.OpenId }>,
    'provider'
  >
) {
  const superEnabled = useIsSuperEnabledFromUrl()
  const loginOrigin = getSearchParams({ superEnabled })
  const pathname = `/api/auth/openId/login?${loginOrigin}`
  return (
    <OAuthLink {...props} pathname={pathname}>
      <FormattedMessage
        id="boarding.signInWith.openId"
        defaultMessage="Continue with {provicerName}"
        values={{
          provicerName: props.authProvider.name,
        }}
      />
    </OAuthLink>
  )
}

function Apple(props: Omit<SignInWithProviderProps, 'provider'>) {
  const superEnabled = useIsSuperEnabledFromUrl()
  const loginOrigin = getSearchParams({ superEnabled })
  const pathname =
    props.organizationId || props.organizationInviteToken
      ? `/api/auth/apple/login`
      : '/api/auth/apple/signup'
  return (
    <OAuthLink
      {...props}
      OauthIcon={AppleIcon}
      pathname={`${pathname}?${loginOrigin}`}
    >
      <FormattedMessage
        id="boarding.signInWith.apple"
        defaultMessage="Continue with Apple"
        values={{}}
      />
    </OAuthLink>
  )
}

type SignInWithProviderProps = QueryArgs & {
  onClick: OnClickCB
  className?: string
  organizationId?: string
  iconOnly?: boolean
  superEnabled: boolean
} & (
    | {
        provider: Exclude<SSOProviders, SSOProviders.OpenId>
      }
    | {
        provider: SSOProviders.OpenId
        authProvider: { name: string }
      }
  )

export function SignInWithProvider({
  provider,
  ...props
}: SignInWithProviderProps) {
  switch (provider) {
    case SSOProviders.Google:
      return <Google {...props} />
    case SSOProviders.Slack:
      return <Slack {...props} />
    case SSOProviders.OpenId:
      if ('authProvider' in props) {
        return <OpenId {...props} />
      }
      return null
    case SSOProviders.Apple:
      return <Apple {...props} />

    default:
      throw new Error(`Unknown provider ${provider}`)
  }
}
